<template>
  <div>
    <NavBar>
      <template v-slot:desktop-nav>
        <div v-cloak class="d-flex align-items-center" v-if="list">
          <ItemSearch v-if="hasRole(Role.MEMBER)" :listId="id" />
          <router-link v-if="hasRole(Role.MEMBER)" class="btn btn-yellow btn-nav my-2 my-sm-0 ml-2" :to="'/lists/' + list.id + '/order'">
            <i class="fas fa-sort-amount-up"></i>
          </router-link>
          <a v-if="hasRole(Role.OWNER)" class="btn btn-yellow btn-nav my-2 my-sm-0 ml-2" data-testid="manage-members" data-toggle="modal" data-target="#user-management">
            <i class="fas fa-users"></i>
          </a>
          <a v-if="hasRole(Role.OWNER)" class="btn btn-yellow btn-nav my-2 my-sm-0 ml-2" data-toggle="modal" data-target="#list-create-modal">
            <i class="fas fa-cog" aria-hidden="true"></i>
          </a>
          <a v-if="isMember" class="btn btn-red btn-nav-red my-2 my-sm-0 ml-2" @click="leaveList">
            <i class="fas fa-running"></i>
          </a>
        </div>
      </template>
      <template v-slot:mobile-nav>
        <router-link class="mobile-nav-icon btn-nav btn btn-yellow w-100" to="/lists">
          <i class="fas fa-arrow-alt-circle-left"></i>
          Back
        </router-link>
        <router-link
          v-if="list != null && hasRole(Role.MEMBER)"
          class="mobile-nav-icon btn-nav-yellow btn btn-yellow w-100"
          :to="'/lists/' + list.id + '/order'">
          <i class="fas fa-sort-amount-up"></i>
          Order
        </router-link>
        <a
          v-if="list != null && hasRole(Role.OWNER)"
          data-toggle="modal"
          data-target="#user-management"
          class="mobile-nav-icon btn-nav-yellow btn btn-yellow w-100">
          <i class="fas fa-users"></i>
          Members
        </a>
        <div
          v-if="list != null && hasRole(Role.OWNER)"
          class="mobile-nav-icon btn-nav-yellow btn btn-yellow w-100"
          data-toggle="modal"
          data-target="#list-create-modal">
          <i class="fas fa-cog"></i>
          Settings
        </div>
        <div v-if="list != null && isMember" class="mobile-nav-icon btn-nav-yellow btn btn-yellow w-100" @click="leaveList">
          <i class="fas fa-running"></i>
          Leave
        </div>
      </template>
    </NavBar>
    <div v-cloak class="container home" v-if="list">
      <div class="movie-list-header">
        <h1>{{ list.listName }} List</h1>
      </div>

      <ItemSearch v-if="hasRole(Role.MEMBER)" class="w-100 mb-3 d-md-none" :listId="id" />

      <EmptyState
        v-if="isOwnerAlone"
        title="You’re the only person in this list"
        description="Copy an invite link so friends, your partner, or your household can help decide.">
        <template #action>
          <button class="btn btn-green" type="button" data-toggle="modal" data-target="#user-management">
            Invite people
          </button>
        </template>
      </EmptyState>

      <div v-cloak class="alert alert-warning" role="alert" v-if="unrankedItems.length">
        <h4 class="alert-heading">You still have titles to rank</h4>
        <p class="m-0">
          Rank these titles to improve the group result.
          <router-link :to="'/lists/' + list.id + '/order'" class="alert-link">Rank titles now</router-link>.
        </p>
      </div>

      <section v-if="isRankedList && hasGroupResult" class="current-choice" data-testid="group-result" aria-labelledby="current-choice-heading">
        <h2 id="current-choice-heading">Current group choice</h2>
        <p class="group-result-explanation">The top pick is the title your group ranked highest overall. Lower average rank means stronger group preference.</p>
        <ItemCardLarge
          :item="currentGroupChoice.item"
          :listId="list.id"
          :dateAdded="currentGroupChoice.createdAt"
          :canMarkWatched="canMarkWatched"
          :markingWatched="markingWatchedId === currentGroupChoice.item.id"
          @mark-watched="markAsWatched(currentGroupChoice)" />
        <p class="group-result-participation" data-testid="ranking-participation">
          <span v-if="rankingParticipationText">{{ rankingParticipationText }} &middot; </span>Average rank: {{ currentGroupChoiceAverageRank }}
        </p>
      </section>

      <section v-else-if="isRankedList && hasActiveTitles" class="group-result" data-testid="group-result" aria-labelledby="group-result-heading">
        <h2 id="group-result-heading">Group result</h2>
        <div class="alert alert-info" role="status">
          <h4 class="alert-heading">No rankings yet</h4>
          <p class="mb-0">
            Rank the titles to see what your group prefers.
            <router-link :to="'/lists/' + list.id + '/order'" class="alert-link">Rank titles now</router-link>.
          </p>
        </div>
      </section>

      <EmptyState
        v-if="!unrankedLoading && !hasActiveTitles"
        title="No titles yet"
        description="Add a few movies or TV series so your group can start ranking.">
        <template #action>
          <ItemSearch v-if="hasRole(Role.MEMBER)" :listId="id" />
        </template>
      </EmptyState>

      <section v-if="isRankedList && hasGroupResult" class="other-active-titles" data-testid="active-titles" aria-labelledby="other-active-heading">
        <h2 id="other-active-heading">
          Other active titles
          <span aria-hidden="true">&middot;</span>
          {{ otherActiveTitles.length }}
        </h2>
        <ItemCardLarge
          v-for="item in otherActiveTitles"
          :key="item.id"
          :item="item.item"
          :listId="list.id"
          :dateAdded="item.createdAt"
          :canMarkWatched="canMarkWatched"
          :markingWatched="markingWatchedId === item.item.id"
          @mark-watched="markAsWatched(item)" />
        <p v-if="otherActiveTitles.length === 0" class="no-other-titles">No other active titles yet.</p>
      </section>

      <section v-else data-testid="active-titles" aria-label="Active titles">
        <ItemCardLarge
          v-for="item in list.listItems"
          :key="item.id"
          :item="item.item"
          :listId="list.id"
          :dateAdded="item.createdAt"
          :canMarkWatched="canMarkWatched"
          :markingWatched="markingWatchedId === item.item.id"
          @mark-watched="markAsWatched(item)" />
      </section>

      <div v-if="undoWatchedItem" class="alert alert-success watched-undo" role="status" data-testid="watched-undo">
        <span>Marked as watched.</span>
        <button class="btn btn-link alert-link p-0 ml-2" type="button" :disabled="restoringItemId !== null" @click="undoMarkWatched">
          {{ restoringItemId === undoWatchedItem.item.id ? "Undoing..." : "Undo" }}
        </button>
      </div>

      <section v-if="canViewWatched" class="watched-section" data-testid="watched-archive" aria-labelledby="watched-heading">
        <h2 id="watched-heading">
          Watched
          <span aria-hidden="true">&middot;</span>
          {{ watchedItems.length }}
        </h2>

        <p v-if="watchedLoading" class="text-center">Loading watched titles...</p>

        <div v-else-if="watchedError" class="alert alert-danger" role="alert">
          <span>Unable to load watched titles.</span>
          <button class="btn btn-yellow btn-sm ml-2" type="button" @click="loadWatchedItems">Try again</button>
        </div>

        <EmptyState
          v-else-if="watchedItems.length === 0"
          title="No titles watched yet"
          description="Watched titles will appear here after your group marks something as watched." />

        <template v-else>
          <ItemCardLarge
            v-for="item in visibleWatchedItems"
            :key="item.id"
            :item="item.item"
            :listId="list.id"
            :dateAdded="item.watchedAt"
            datePrefix="watched"
            :canRestore="canMarkWatched"
            :restoring="restoringItemId === item.item.id"
            @restore-active="restoreToActive(item)" />
          <button
            v-if="hasMoreWatchedItems"
            class="btn btn-yellow watched-toggle"
            type="button"
            :aria-expanded="showAllWatched ? 'true' : 'false'"
            @click="showAllWatched = !showAllWatched">
            {{ showAllWatched ? "Show fewer" : "Show all watched titles" }}
          </button>
        </template>
      </section>

      <ListSettings v-if="hasRole(Role.OWNER)" :userRole="userRole" :list="list"></ListSettings>

      <UserManagement v-if="hasRole(Role.OWNER)" :id="list.id" @member-count="memberCount = $event" />

      <DeleteConfirm
        @delete-confirmed="leaveListConfirm"
        @closed="openConfirm = false"
        :value="list.listName"
        :title="'Leave ' + list.listName"
        text="Please retype the name in order to leave"
        buttonText="Leave"
        :open="openConfirm" />
    </div>
  </div>
</template>

<script>
import ItemCardLarge from "@/components/ItemCardLarge.vue";
import DeleteConfirm from "@/components/DeleteConfirm";
import NavBar from "@/components/NavBar.vue";
import ListSettings from "@/components/ListSettings.vue";
import ListService from "../../services/ListService";
import ItemSearch from "@/components/ItemSearch.vue";
import UserManagement from "./UserManagement";
import { computed } from "vue";
import { Role, hasRole } from "../../utilities/list.role";
import { userId } from "../../store/helpers";
import ListType from "../../utilities/listTypes";
import EmptyState from "@/components/EmptyState.vue";

const WATCHED_COMPACT_LIMIT = 3;

export default {
  name: "ListView",
  props: ["id"],
  data() {
    return {
      list: null,
      userRole: null,
      unrankedItems: [],
      unrankedLoading: true,
      watchedItems: [],
      watchedLoading: false,
      watchedError: false,
      showAllWatched: false,
      markingWatchedId: null,
      restoringItemId: null,
      undoWatchedItem: null,
      undoTimer: null,
      memberCount: null,
      Role,
      openConfirm: false,
    };
  },
  computed: {
    ...userId,
    isMember() {
      return this.userRole && this.userRole.id === 3;
    },
    canMarkWatched() {
      return this.userRole && hasRole(this.userRole.id, Role.MEMBER);
    },
    canViewWatched() {
      return this.canMarkWatched;
    },
    visibleWatchedItems() {
      return this.showAllWatched ? this.watchedItems : this.watchedItems.slice(0, WATCHED_COMPACT_LIMIT);
    },
    hasMoreWatchedItems() {
      return this.watchedItems.length > WATCHED_COMPACT_LIMIT;
    },
    isRankedList() {
      return this.list && Number(this.list.listTypeId) === ListType.RANKED;
    },
    hasGroupResult() {
      return this.isRankedList && this.list.listItems.length > 0;
    },
    currentGroupChoice() {
      return this.hasGroupResult ? this.list.listItems[0] : null;
    },
    currentGroupChoiceAverageRank() {
      const averageRank = Number(this.currentGroupChoice && this.currentGroupChoice.score);
      return Number.isFinite(averageRank) ? averageRank.toFixed(1) : "Not available";
    },
    otherActiveTitles() {
      return this.hasGroupResult ? this.list.listItems.slice(1) : [];
    },
    rankingParticipationText() {
      const participation = this.list && this.list.rankingParticipation;

      if (!participation) {
        return "";
      }

      const rankedCount = Number(participation.rankedMemberCount) || 0;
      const totalCount = Number(participation.totalMemberCount) || 0;

      if (rankedCount === 0) {
        return "No rankings yet";
      }

      if (totalCount === 1) {
        return "Ranked by 1 member";
      }

      const status = `Ranked by ${rankedCount} of ${totalCount} members`;
      return participation.isProvisional ? `Provisional result — ${status.toLowerCase()}` : status;
    },
    hasActiveTitles() {
      return this.list.listItems.length > 0 || this.unrankedItems.length > 0;
    },
    isOwnerAlone() {
      return this.hasRole(Role.OWNER) && this.memberCount === 1;
    },
  },
  provide() {
    return {
      userRole: computed(() => this.userRole),
    };
  },
  methods: {
    hasRole(role) {
      return this.userRole && hasRole(this.userRole.id, role);
    },
    loadWatchedItems() {
      this.watchedLoading = true;
      this.watchedError = false;

      return ListService.getWatchedItems(this.id)
        .then((items) => {
          this.watchedItems = items;
        })
        .catch((error) => {
          this.watchedError = true;
          return error.handleError();
        })
        .finally(() => {
          this.watchedLoading = false;
        });
    },
    loadActiveState() {
      return ListService.getList(this.id).then((result) => {
        this.list = result.list;

        if (this.hasRole(Role.MEMBER) && this.isRankedList) {
          this.unrankedLoading = true;
          return ListService.getUnrankedItems(this.id)
            .then((items) => {
              this.unrankedItems = items;
            })
            .finally(() => {
              this.unrankedLoading = false;
            });
        }

        this.unrankedItems = [];
        this.unrankedLoading = false;
        return null;
      });
    },
    showUndo(listItem) {
      if (this.undoTimer) {
        clearTimeout(this.undoTimer);
      }

      this.undoWatchedItem = listItem;
      this.undoTimer = setTimeout(() => {
        this.undoWatchedItem = null;
        this.undoTimer = null;
      }, 8000);
    },
    clearUndo() {
      if (this.undoTimer) {
        clearTimeout(this.undoTimer);
      }

      this.undoWatchedItem = null;
      this.undoTimer = null;
    },
    markAsWatched(listItem) {
      if (this.markingWatchedId !== null) {
        return;
      }

      this.markingWatchedId = listItem.item.id;

      ListService.markItemWatched(this.id, listItem.item.id)
        .then(() => {
          this.list.listItems = this.list.listItems.filter((item) => item.id !== listItem.id);
          this.unrankedItems = this.unrankedItems.filter((item) => item.id !== listItem.item.id);
          this.showUndo(listItem);
          return this.loadWatchedItems();
        })
        .catch((error) => {
          return error.handleError();
        })
        .finally(() => {
          this.markingWatchedId = null;
        });
    },
    undoMarkWatched() {
      if (this.undoWatchedItem) {
        return this.restoreToActive(this.undoWatchedItem);
      }
    },
    restoreToActive(listItem) {
      if (this.restoringItemId !== null) {
        return;
      }

      this.restoringItemId = listItem.item.id;

      return ListService.restoreItemToActive(this.id, listItem.item.id)
        .then(() => {
          this.clearUndo();
          return Promise.all([this.loadActiveState(), this.loadWatchedItems()]);
        })
        .then(() => {
          this.$toast.success(`${listItem.item.title} returned to the active list`);
        })
        .catch(() => {
          this.$toast.error(`Unable to return ${listItem.item.title} to the active list. Please try again.`);
        })
        .finally(() => {
          this.restoringItemId = null;
        });
    },
    leaveList() {
      this.openConfirm = true;
    },
    leaveListConfirm() {
      ListService.deleteUser(this.list.id, this.userId)
        .then(() => {
          this.$toast.success("Successfully Left " + this.list.listName);

          this.$router.push({ path: "/lists" });
        })
        .catch((error) => {
          return error.handleError();
        });
    },
  },
  components: {
    ItemCardLarge,
    NavBar,
    ListSettings,
    ItemSearch,
    UserManagement,
    DeleteConfirm,
    EmptyState,
  },
  mounted() {
    if (this.hasRole(Role.MEMBER) && this.list.listTypeId == ListType.RANKED) {
      ListService.getUnrankedItems(this.id)
        .then((items) => {
          this.unrankedItems = items;
        })
        .catch((error) => {
          return error.handleError();
        })
        .finally(() => {
          this.unrankedLoading = false;
        });
    } else {
      this.unrankedLoading = false;
    }
  },
  beforeUnmount() {
    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
    }
  },
  beforeRouteEnter(_to, _from, next) {
    ListService.getList(_to.params.id)
      .then((result) => {
        next((vm) => {
          vm.list = result.list;

          vm.userRole = result.userRole;

          if (vm.canViewWatched) {
            vm.loadWatchedItems();
          }
        });
      })
      .catch((error) => {
        const status = error.response ? error.response.status : null;

        if (status === 403) {
          return next({ name: "Forbidden", replace: true });
        }

        if (status === 404) {
          return next({ name: "Missing Resource", replace: true });
        }

        return error.handleError();
      });
  },
};
</script>

<style scoped>
.search-box {
  border-radius: 0;
  color: #dedede;
  border: none;
  background-color: #6a72a0;
  background-image: url("https://www.transparenttextures.com/patterns/tweed.png");
  /* This is mostly intended for prototyping; please download the pattern and re-host for production environments. Thank you! */
  box-shadow: 5px 5px #4d5061;
  border: solid #4d5061;
  font-family: "Raleway", sans-serif;
  font-weight: bold;
  margin-right: 20px !important;
}

.search-box:hover {
  box-shadow: 5px 5px #c5a531;
  border-color: #e8c547;
}

.search-box:focus {
  background-color: #6a72a0;
  box-shadow: 0 0 0 0.2rem #c5a531;
  border-color: #e8c547;
  color: #e8c547;
}

.search-box::placeholder {
  color: #dedede;
}

.watched-section {
  border-top: 3px solid #393d55;
  margin-top: 3rem;
  padding-top: 1.5rem;
}

.group-result,
.current-choice,
.other-active-titles {
  margin: 1.5rem 10px 0;
}

.group-result h2,
.current-choice h2,
.other-active-titles h2 {
  margin-bottom: 0.25rem;
}

.current-choice {
  background-color: rgba(77, 80, 97, 0.45);
  border: 2px solid #e8c547;
  padding: 1rem;
}

.current-choice .item-card-container {
  margin: 0 -10px;
}

.other-active-titles {
  border-top: 3px solid #393d55;
  margin-top: 2rem;
  padding-top: 1.5rem;
}

.no-other-titles {
  color: #c7cad8;
  margin-top: 1rem;
}

.group-result-explanation {
  color: #dedede;
  margin-bottom: 0;
}

.group-result-participation {
  color: #c7cad8;
  font-size: 0.9rem;
  margin: 0.75rem 0 0;
}

.watched-section h2 {
  margin-left: 10px;
}

.watched-toggle {
  display: block;
  margin: 1rem 10px 0;
}

@media (max-width: 575.98px) {
  .watched-toggle {
    margin-left: 10px;
    margin-right: 10px;
    width: calc(100% - 20px);
  }
}

.search-box::-webkit-search-cancel-button {
  position: relative;
  right: 20px;
  content: "wefwe";

  -webkit-appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 10px;
  /* background: red; */
}
</style>
